/**
 * Utilities for building human-readable Admin Log messages.
 *
 * These helpers turn raw entity data (IDs, booleans, nulls, enums, dates, etc.)
 * into readable summaries and a structured "changes"/"details" breakdown that is
 * carried inside the log payload and rendered in the Admin Logs detail view.
 *
 * They never throw: logging is best-effort and must never break the main flow.
 */

// ============================================================================
// TYPES
// ============================================================================

export type BoolStyle = 'enabledDisabled' | 'yesNo' | 'activeInactive';

export type FieldType =
  | 'text'
  | 'boolean'
  | 'enum'
  | 'date'
  | 'list'
  | 'number';

export interface FieldSpec {
  /** Key on the entity object. */
  key: string;
  /** Human-readable label shown in the log. */
  label: string;
  /** How the value should be rendered. Defaults to 'text'. */
  type?: FieldType;
  /** Rendering style for boolean values. */
  boolStyle?: BoolStyle;
  /** Map raw enum/id values to friendly labels. */
  map?: Record<string | number, string>;
  /** Custom resolver (e.g. id -> related entity name). Takes precedence over map. */
  resolve?: (value: any) => string;
}

/** A single change entry (value change OR list add/remove). */
export interface LogChange {
  label: string;
  from?: string;
  to?: string;
  added?: string[];
  removed?: string[];
}

/** A single detail entry used for create/delete logs. */
export interface LogDetail {
  label: string;
  value: string;
}

// ============================================================================
// VALUE HUMANIZATION
// ============================================================================

const NOT_SET = 'Not Set';
const NONE = 'None';

function formatBoolean(value: boolean, style: BoolStyle = 'enabledDisabled'): string {
  switch (style) {
    case 'yesNo':
      return value ? 'Yes' : 'No';
    case 'activeInactive':
      return value ? 'Active' : 'Inactive';
    case 'enabledDisabled':
    default:
      return value ? 'Enabled' : 'Disabled';
  }
}

function coerceBoolean(value: any): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes' || v === 'active' || v === 'enabled';
  }
  return Boolean(value);
}

function formatDate(value: any): string {
  try {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(value);
  }
}

/**
 * Convert a raw value into a readable string using an optional FieldSpec.
 */
export function humanizeValue(value: any, spec?: Partial<FieldSpec>): string {
  try {
    // Resolve/map each list element individually (never pass the whole array to resolve).
    if (Array.isArray(value)) {
      if (value.length === 0) return NONE;
      const itemSpec: Partial<FieldSpec> = {
        ...spec,
        type: spec?.type === 'list' ? 'text' : spec?.type,
      };
      return value
        .map((item) => {
          const key =
            item && typeof item === 'object'
              ? item.id ?? item.value ?? item.name ?? item
              : item;
          return humanizeValue(key, itemSpec);
        })
        .join(', ');
    }

    if (spec?.resolve) {
      const resolved = spec.resolve(value);
      if (resolved !== undefined && resolved !== null && resolved !== '') {
        return String(resolved);
      }
    }

    if (value === null || value === undefined || value === '') {
      return NOT_SET;
    }

    if (spec?.map && (typeof value === 'string' || typeof value === 'number')) {
      const mapped = spec.map[value];
      if (mapped !== undefined) return mapped;
    }

    if (spec?.type === 'boolean' || typeof value === 'boolean') {
      return formatBoolean(coerceBoolean(value), spec?.boolStyle);
    }

    if (spec?.type === 'date') {
      return formatDate(value);
    }

    if (typeof value === 'object') {
      return value.name || value.label || value.display_name || JSON.stringify(value);
    }

    return String(value);
  } catch {
    return String(value ?? NOT_SET);
  }
}

/**
 * Format an entity reference as "<id> (<name>)" for log detail values, e.g.
 * formatIdWithName(506, 'Zuri') -> '506 (Zuri)'. When no name is available it
 * returns just the id. Pair this with a label that carries the entity + "ID"
 * (e.g. { label: 'Brand ID', value: formatIdWithName(506, 'Zuri') }).
 */
export function formatIdWithName(
  id: string | number | null | undefined,
  name?: string | null
): string {
  const idPart = id === null || id === undefined || id === '' ? NOT_SET : String(id);
  const trimmedName = typeof name === 'string' ? name.trim() : '';
  return trimmedName ? `${idPart} (${trimmedName})` : idPart;
}

// ============================================================================
// DIFFING
// ============================================================================

function valuesEqual(a: any, b: any, type?: FieldType): boolean {
  if (type === 'boolean') {
    return coerceBoolean(a) === coerceBoolean(b);
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    const arrA = Array.isArray(a) ? a : [];
    const arrB = Array.isArray(b) ? b : [];
    if (arrA.length !== arrB.length) return false;
    return arrA.every((item, i) => item === arrB[i]);
  }
  if (a === null || a === undefined) a = '';
  if (b === null || b === undefined) b = '';
  return String(a) === String(b);
}

/**
 * Compare an old and new entity across the provided field specs and return
 * only the fields that actually changed, with humanized from/to values.
 */
export function diffFields(
  oldObj: Record<string, any> = {},
  newObj: Record<string, any> = {},
  specs: FieldSpec[]
): LogChange[] {
  const changes: LogChange[] = [];

  for (const spec of specs) {
    try {
      const oldVal = oldObj?.[spec.key];
      const newVal = newObj?.[spec.key];

      if (spec.type === 'list') {
        const { added, removed } = diffList(oldVal, newVal, spec);
        if (added.length || removed.length) {
          changes.push({ label: spec.label, added, removed });
        }
        continue;
      }

      if (!valuesEqual(oldVal, newVal, spec.type)) {
        changes.push({
          label: spec.label,
          from: humanizeValue(oldVal, spec),
          to: humanizeValue(newVal, spec),
        });
      }
    } catch {
      // ignore malformed field, keep logging best-effort
    }
  }

  return changes;
}

/**
 * Diff two lists (of primitives or objects) and return added/removed labels.
 */
export function diffList(
  oldArr: any[] = [],
  newArr: any[] = [],
  spec?: Partial<FieldSpec>
): { added: string[]; removed: string[] } {
  const toLabel = (item: any): string => {
    if (item && typeof item === 'object') {
      const key = item.id ?? item.value ?? item.name;
      return humanizeValue(key ?? item, spec);
    }
    return humanizeValue(item, spec);
  };

  const oldList = Array.isArray(oldArr) ? oldArr : [];
  const newList = Array.isArray(newArr) ? newArr : [];

  const oldLabels = oldList.map(toLabel);
  const newLabels = newList.map(toLabel);

  const added = newLabels.filter((l) => !oldLabels.includes(l));
  const removed = oldLabels.filter((l) => !newLabels.includes(l));

  return { added, removed };
}

// ============================================================================
// DETAILS (create / delete)
// ============================================================================

/**
 * Build a list of readable detail rows from an entity for create/delete logs.
 * Fields whose value is Not Set can optionally be skipped.
 */
export function buildDetails(
  obj: Record<string, any> = {},
  specs: FieldSpec[],
  options: { skipEmpty?: boolean } = {}
): LogDetail[] {
  const details: LogDetail[] = [];

  for (const spec of specs) {
    try {
      const value = humanizeValue(obj?.[spec.key], spec);
      if (options.skipEmpty && (value === NOT_SET || value === NONE)) continue;
      details.push({ label: spec.label, value });
    } catch {
      // ignore malformed field
    }
  }

  return details;
}

// ============================================================================
// SUMMARY
// ============================================================================

export type LogActionVerb = 'created' | 'updated' | 'deleted' | 'activated' | 'deactivated';

/**
 * Build a concise one-line summary suitable for the logs table.
 * e.g. buildSummary('Brand', 'Nike US', 'updated', 3)
 *        -> 'Brand "Nike US" updated (3 fields changed)'
 */
export function buildSummary(
  entity: string,
  name: string | undefined | null,
  action: LogActionVerb,
  changeCount?: number
): string {
  const namePart = name ? ` "${name}"` : '';
  let summary = `${entity}${namePart} ${action}`;

  if (action === 'updated' && typeof changeCount === 'number') {
    if (changeCount === 0) {
      summary += ' (no changes)';
    } else {
      summary += ` (${changeCount} ${changeCount === 1 ? 'field' : 'fields'} changed)`;
    }
  }

  return summary;
}

/**
 * Build a one-line human-readable representation of a list of changes,
 * useful when a compact inline string is preferred over the structured view.
 */
export function summarizeChanges(changes: LogChange[]): string {
  return changes
    .map((c) => {
      if (c.added || c.removed) {
        const parts: string[] = [];
        if (c.added?.length) parts.push(`+${c.added.join(', ')}`);
        if (c.removed?.length) parts.push(`-${c.removed.join(', ')}`);
        return `${c.label}: ${parts.join(' ')}`;
      }
      return `${c.label}: ${c.from} -> ${c.to}`;
    })
    .join('; ');
}
